/**
 * Config for the one-time onboarding tour that runs the first time a
 * super-admin opens the Demo Toolkit on the Acme tenant.
 *
 * Each step walks the user through one of the 5 tabs and optionally
 * pulses specific elements (by `data-testid`) so the SE sees exactly
 * which button the copy is referring to — not just reads about it.
 *
 * Keep this file a plain data module (no imports besides types). The
 * component consumes it as-is; adding new steps or adjusting copy
 * should never require touching `DemoToolkit.jsx`.
 *
 * Shape of each step:
 *   tab                    — which tab to auto-focus before showing this step
 *   title                  — "N / M · TAB" header copy
 *   body                   — 1–2 sentences of narrative
 *   testids_to_highlight   — optional list of testids to pulse during this step
 */
export const DEMO_TOOLKIT_TOUR_STEPS = [
  {
    tab: 'session',
    title: '1 / 5 · Session',
    body: 'Start every demo here. "Rehearse" ships you to a pristine tenant in a new tab. "Record session" also captures each click for a narration later. "Reset only" wipes without opening a tab.',
    testids_to_highlight: [
      'toolkit-rehearse-demo-btn',
      'toolkit-record-session-btn',
      'toolkit-reset-acme-btn',
    ],
  },
  {
    tab: 'capture',
    title: '2 / 5 · Capture',
    body: 'Grab the current state — PNG screenshot, full-page JSON snapshot, or a 24-hour shareable demo link for prospects.',
    testids_to_highlight: [
      'toolkit-capture-png-btn',
      'toolkit-copy-json-btn',
      'toolkit-copy-demo-link-btn',
    ],
  },
  {
    tab: 'seed',
    title: '3 / 5 · Seed',
    body: 'Trigger narrative anomalies live during the call (red flashing tile) or clear any you injected without a page reload.',
    testids_to_highlight: [
      'toolkit-trigger-anomaly-btn',
      'toolkit-clear-anomalies-btn',
    ],
  },
  {
    tab: 'narration',
    title: '4 / 5 · Narration',
    body: 'After recording a session, hand the click log to GPT-5.2 → voice-over script → TTS MP3 → save as a reusable sales preset. Everything chains in order.',
    // Narration buttons only render when lastRecording exists — pulsing the
    // empty-state CTA teaches new users where to start when they have nothing yet.
    testids_to_highlight: [
      'narration-empty-state-goto-session',
      'toolkit-generate-narration-btn',
    ],
  },
  {
    tab: 'actions',
    title: '5 / 5 · Actions',
    body: "Navigate to Anomaly Dashboard / Data Health, send the weekly digest on-demand, browse saved presets, and open the Demo Story Director to dial Acme's narrative arc.",
    testids_to_highlight: [
      'toolkit-open-director-btn',
      'toolkit-send-digest-btn',
      'toolkit-presets-toggle-btn',
    ],
  },
];

/** localStorage key for "has this super-admin seen the tour at least once?" */
export const TOUR_SEEN_KEY = 'demo_toolkit_tour_seen_v1';

/** CSS class the component toggles on `data-testid` matches to pulse them. */
export const TOUR_PULSE_CLASS = 'toolkit-tour-pulse-on';
